import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

const CatrionaChildProfile = () => {
  const person = {
    id: "catriona-child",
    name: "Catriona Child",
    honorific: "Ms",
    title: "Executive Director",
    affiliation: "The Highland Institute, Kohima, Nagaland",
    image: "https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/exv37i34_Catriona%20Child.jpg",
    email: null,
    category: "Leadership Council"
  };

  const SectionDivider = () => (
    <div className="my-12 flex items-center justify-center">
      <div className="h-px w-24 bg-stone-300"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="catriona-child-profile">
      {/* Breadcrumb Navigation */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center text-sm text-stone-600">
            <Link to="/" className="hover:text-green-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/people" className="hover:text-green-600 transition-colors">People</Link>
            <span className="mx-2">/</span>
            <span className="text-stone-900">{person.name}</span>
          </nav>
        </div>
      </div>

      {/* Identity Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16 border-b border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-lg overflow-hidden bg-stone-200 shadow-lg">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium uppercase tracking-wider rounded-full">
                    {person.category}
                  </span>
                </div>
                <h1 className="text-4xl font-serif font-bold text-stone-900 mb-2">
                  <span className="text-stone-600">{person.honorific} </span>
                  {person.name}
                </h1>
                <p className="text-xl text-stone-700 font-medium mb-4">
                  {person.title}
                </p>
                <p className="text-stone-600 leading-relaxed">
                  {person.affiliation}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">

          {/* Intellectual Biography */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Intellectual Biography
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Catriona Child is Executive Director of The Highland Institute (THI), Kohima, Nagaland. Her work focuses on institutional leadership, research administration, mentoring, ethics, writing, publication strategy, and programme development. She supports and enables field-based scholarship in Northeast India by helping to build the institutional, editorial, and collaborative structures through which researchers, analysts, students, and community partners can carry out their work.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                Her role at The Highland Institute centres on strategic coordination, grant development, academic partnerships, project oversight, staff mentoring, and the dissemination of research through publications, reports, journals, and public-facing outputs. She works closely with THI analysts and collaborators, supporting project design, editorial development, supervision, and institutional planning.
              </p>
              <p className="text-stone-700 leading-relaxed">
                Child has long-standing experience in academic editing, environmental and public health communication, institutional development, and community-based academic work in Northeast India. Her earlier professional background includes editorial leadership in environmental publishing, communications consultancy, and publication support for international organisations, academic journals, and research institutions.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Areas of Interest */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Areas of Interest
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 font-medium mb-4">Her areas of interest include:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Administration and institutional development</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Academic writing, editing, and publication strategy</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Mentoring and postgraduate support</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Ethics, permissions, and responsible research practice</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Community-based academic work in Northeast India</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Environmental, health, and policy communication</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Oral history, archival methods, and documentation support</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Interdisciplinary collaboration and knowledge dissemination</span></li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Highland Institute Projects */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Highland Institute Projects
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                At The Highland Institute, Child has contributed to the development, coordination, and dissemination of a wide range of interdisciplinary projects. As Executive Director, she leads institutional strategy, supports research teams, develops partnerships, and oversees academic and public-facing outputs.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                She has supported projects connected to Indigenous knowledge systems, environmental humanities, public health, oral history, visual archives, documentary work, and climate change. Her role has included grant development, project coordination, supervision of staff and volunteers, editorial guidance, and support for collaborative research processes.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                She was <strong>Project Coordinator for the Earthkeepers Project</strong>, funded by IDRC, Canada, which documented traditional ecological knowledge and climate change perceptions along the Myanmar–Nagaland border. Her role focused on coordination, documentation support, community collaboration, and the development of participatory approaches.
              </p>
              <p className="text-stone-700 leading-relaxed">
                She also served as <strong>Project Manager for the Global Health Research Group project</strong> on surgical capacity and access in Northeast India, which resulted in a peer-reviewed publication in <em>PLOS ONE</em> in 2024.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Selected Publications & Editorial Outputs */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Selected Publications and Editorial Outputs
            </h2>
            <div className="prose prose-stone max-w-none">
              <ul className="space-y-4">
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2024</span> &mdash; 'How ready is the health care system in Northeast India for surgical delivery? A mixed-methods study on surgical capacity and need' in <em>PLOS ONE</em>, Vol. 19, Issue 6, Article e0287941.{' '}
                  <a
                    href="https://doi.org/10.1371/journal.pone.0287941"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-700 hover:text-green-800 underline break-all"
                  >
                    https://doi.org/10.1371/journal.pone.0287941
                  </a>
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2021</span> &mdash; 'Conversation Pieces: How Digital Technologies Might Reinvigorate and Reveal the Social Lives of Objects' in <em>Materiality and Visuality in North East India</em>, Springer.
                </li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Postgraduate Qualifications */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Postgraduate Qualifications
            </h2>
            <ul className="space-y-2">
              <li className="flex items-start gap-3 text-stone-700">
                <span className="text-green-600 mt-1">•</span>
                <span>MSc in Ecology</span>
              </li>
            </ul>
          </section>

          <SectionDivider />

          {/* Contact & Scholarly Infrastructure */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Contact &amp; Scholarly Infrastructure
            </h2>

            <div className="bg-stone-50 p-6 rounded-lg max-w-xl">
              <h3 className="font-semibold text-stone-900 mb-3">Institutional Affiliation</h3>
              <p className="text-stone-700 leading-relaxed text-sm">
                The Highland Institute<br />
                Kohima, Nagaland
              </p>
            </div>

            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Professional Roles</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Executive Director, The Highland Institute</span>
                </li>
              </ul>
            </div>

            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Honorary Positions</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Vice President, Highland Institute Fellowship</span>
                </li>
              </ul>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-stone-50 border-t border-stone-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-stone-500 font-serif italic">
              This profile forms part of The Highland Institute's living scholarly archive, documenting research trajectories, collaborative commitments, and the intellectual work shaping the Institute's wider academic community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatrionaChildProfile;
